import { Button, Card, Checkbox, IconButton, Input, Select, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DataCollectionResponse } from "@/api";
import { errorMessage, getDataSourceQueryKey, listDataSourcesQueryKey, updateDataSourceMutation } from "@/api";
import { Text } from "@/components/Text";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { browserTimezone, DAYS, readSchedule, ScheduleSummary, type ScheduleValue } from "./scheduleSummary";

/**
 * Sync schedule for a SharePoint data source (the Content tab). Gated on the
 * `automatedSyncEnabled` capability and SharePoint sources only, mirroring the
 * old UI's SyncScheduleSection. Daily at a time, or weekly on chosen days.
 */
export function SyncScheduleCard({ source }: Readonly<{ source: DataCollectionResponse }>) {
  const { t } = useTranslation("dataSources");
  const toast = useToast();
  const queryClient = useQueryClient();
  const capabilities = useCapabilities();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ScheduleValue>(
    () => readSchedule(source) ?? { type: "daily", runAtTime: "09:00", daysOfWeek: [] },
  );

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getDataSourceQueryKey({ path: { collection_uuid: source.uuid } }) }),
      queryClient.invalidateQueries({ queryKey: listDataSourcesQueryKey() }),
    ]);

  const save = useMutation({
    ...updateDataSourceMutation(),
    onSuccess: async () => {
      await invalidate();
      toast.success(t("sync.saved"));
      setEditing(false);
    },
    onError: (e) => toast.error(errorMessage(e, t("sync.saveFailed"))),
  });

  // Backend-gated + SharePoint-only (matches the old UI's control).
  if (!capabilities.automatedSyncEnabled || source.sourceType !== "sharepoint") return null;

  const schedule = readSchedule(source);

  const saveSchedule = (value: ScheduleValue | null) => {
    save.mutate({
      path: { collection_uuid: source.uuid },
      body: {
        syncSchedule: value
          ? {
              type: value.type,
              runAtTime: value.runAtTime,
              timezone: browserTimezone(),
              ...(value.type === "weekly" ? { daysOfWeek: value.daysOfWeek } : {}),
            }
          : null,
      },
    });
  };

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-content-secondary">
            <Clock size={16} aria-hidden />
            <Text variant="body">{t("sync.title")}</Text>
          </div>
          {!editing && (
            <div className="flex items-center gap-1">
              <IconButton
                icon={<Pencil />}
                aria-label={schedule ? t("sync.edit") : t("sync.add")}
                variant="ghost"
                size="sm"
                onClick={() => setEditing(true)}
              />
              {schedule && (
                <IconButton
                  icon={<Trash2 />}
                  aria-label={t("sync.remove")}
                  variant="ghost"
                  size="sm"
                  onClick={() => saveSchedule(null)}
                />
              )}
            </div>
          )}
        </div>

        {editing ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-32">
                <Select
                  size="md"
                  aria-label={t("sync.cadence")}
                  options={[
                    { value: "daily", label: t("sync.daily") },
                    { value: "weekly", label: t("sync.weekly") },
                  ]}
                  value={draft.type}
                  onChange={(v) => setDraft((d) => ({ ...d, type: v as ScheduleValue["type"] }))}
                />
              </div>
              <Input
                size="md"
                aria-label={t("sync.runAt")}
                type="time"
                value={draft.runAtTime}
                onChange={(e) => setDraft((d) => ({ ...d, runAtTime: e.target.value }))}
              />
            </div>
            {draft.type === "weekly" && (
              <div className="flex flex-wrap gap-2">
                {DAYS.map((day, i) => (
                  <Checkbox
                    key={day}
                    label={day}
                    checked={draft.daysOfWeek.includes(i)}
                    onChange={(on) =>
                      setDraft((d) => ({
                        ...d,
                        daysOfWeek: on ? [...d.daysOfWeek, i] : d.daysOfWeek.filter((x) => x !== i),
                      }))
                    }
                  />
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
                {t("content.cancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={save.isPending}
                disabled={draft.type === "weekly" && draft.daysOfWeek.length === 0}
                onClick={() => saveSchedule(draft)}
              >
                {t("sync.saveAction")}
              </Button>
            </div>
          </div>
        ) : (
          <ScheduleSummary schedule={schedule} />
        )}
      </div>
    </Card>
  );
}
